import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { memo, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  CabinPanelBedroomOption_S as CabinPanelBedroomOption,
  CabinPanelSummary_S as CabinPanelSummary,
  ImageInfo_S as ImageInfo,
  LocationAvailabilitySummary_S as LocationAvailabilitySummary,
  Maybe,
  BookingJourneySticky_S as BookingJourneyStickySettings,
} from "@generated/types";
import { BookingJourneyStep, StepperTag } from "../interfaces/booking";
import { DateRange } from "../interfaces/bookingForm";
import { Guests } from "../interfaces/guests";
import { JourneyTypes } from "../interfaces/stepper";
import {
  addTrailingSlashToUrl,
  createPathFromUrl,
  getBookingStepByTag,
  getBookingStepByUrl,
  getBookingStepIndexByTag,
  removeAllGiftVouchersFromCurrentBooking,
} from "../utils";
import {
  formatPrice,
  generateBookingQueryString,
  useNextRouterPathname,
} from "../utils/common";
import {
  RootState,
  setActiveStepperSetting,
  setSelectedLocationIds,
  getBookingConfirmationAction,
  removeUnconfirmedBooking,
  resetActiveBasketAndBooking,
  setBookingBedroom,
  resetReservationFromReferral,
  resetCabinIfo,
  setReachedBookingSummaryPage,
  resetMandatoryExtraInteractions,
} from "@/store/actions";
import {
  bookingPersistedStateSelector,
  useBookingSearchStateSelector,
  reservationExpirySelector,
  reservationStateCabinIdSelector,
  activeStepSelector,
  fhBookingStepsSelector,
  useSiteSettingsSelector,
  stickyCabinBedroomSelector,
  stickyCabinDetailsUrlSelector,
  stickyCabinSelector,
  stickyDateRangeSelector,
  stickyExpirySelector,
  stickyGuestsSelector,
  stickyLocationDetailsUrlSelector,
  stickyLocationSelector,
  bookingJourneyStickySettingsSelector,
  stickyDdaSelector,
  isInterstitialBookingSelector,
  getInterstitialPagePathSelector,
  useVoucherStateSelector,
} from "@/store/selectors";
import { filterXSS } from "xss";
import {
  BookingJourneyStickyIcons,
  BookingJourneyStickyLabels,
  JourneyStickyProps,
} from "@components/SemanticTheme/JourneySticky";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { isBrowser, makeHtml } from "@components/Utils";
import { resetBookingFunnel } from "src/utils/postsst";

const propKey = (props: JourneyStickyProps) =>
  JSON.stringify({
    show: props.show,
    noOfPets: props.noOfPets,
    noOfGuests: props.noOfGuests,
    isoDateFrom: props.isoDateFrom,
    isoDateTo: props.isoDateTo,
    ctaButtonDisabled: props.ctaButtonDisabled,
  });

const DynamicJourneySticky = dynamic(
  () =>
    import("../components/SemanticTheme/JourneySticky").then(
      (module) => module.JourneySticky,
    ),
  { ssr: true },
);

const DynamicFHInlineSvg = dynamic(
  () =>
    import("../components/SemanticTheme/FHInlineSvg").then(
      (module) => module.FHInlineSvg,
    ),
  { ssr: true },
);

const DynamicRichContent = dynamic(
  () =>
    import("../components/SemanticTheme/RichContentArea").then(
      (module) => module.RichContentArea,
    ),
  { ssr: true },
);

export const MemoizedSticky = memo(
  DynamicJourneySticky,
  (prevProps, nextProps) => propKey(prevProps) === propKey(nextProps),
);

type BookingJourneyStickySelectorProps = {
  fhBookingSteps: BookingJourneyStep[];
  activeStep: BookingJourneyStep | null;
  dateRange: DateRange;
  guests: Guests;
  dda: boolean;
  cabin: CabinPanelSummary;
  cabinBedroom: CabinPanelBedroomOption;
  location: LocationAvailabilitySummary;
  cabinDetailsUrl: string;
  locationDetailsUrl: string;
  bookingJourneyStickySettings: BookingJourneyStickySettings;
  reservationStateCabinId: string;
  expiry: string;
  bookingId: string;
  isInterstitialBooking?: boolean;
  interstitialPagePathName?: string;
  experiments?: { [key in string]: string | boolean };
};

export const BookingJourneyStickyComponent: React.FC<
  BookingJourneyStickySelectorProps
> = ({
  dateRange,
  guests,
  dda,
  fhBookingSteps,
  activeStep,
  location,
  cabinDetailsUrl,
  locationDetailsUrl,
  reservationStateCabinId,
  cabin,
  cabinBedroom,
  bookingId,
  bookingJourneyStickySettings,
  isInterstitialBooking,
  interstitialPagePathName,
  experiments,
}: BookingJourneyStickySelectorProps): JSX.Element | null => {
  const history = useRouter();
  const dispatch = useDispatch();
  // component state
  const [isShowing, setIsShowing] = useState(false);
  const [pIIStatusOverride, setpIIStatusOverride] = useState<
    string | undefined
  >(undefined);
  const [pIIDepositTypeOverride, setpIIDepositTypeOverride] = useState<
    string | undefined
  >(undefined);
  const [isOnCabinDetailsPage, setIsOnCabinDetailsPage] = useState(false);
  const [isOnLocationDetailsPage, setIsOnLocationDetailsPage] = useState(false);
  const [isOnLocationsOrCabinsPage, setIsOnLocationsOrCabinsPage] =
    useState(false);

  const { activeBookingStepIndex, reachedBookingSummaryPage } =
    useSiteSettingsSelector();
  const { selectedLocationIds, tempSelectedLocationIds } =
    useBookingSearchStateSelector();
  const { vouchers } = useVoucherStateSelector();

  // steps in the booking journey
  const cabinStepper = getBookingStepByTag(StepperTag.cabins, fhBookingSteps);
  const cabinStepperIndex = getBookingStepIndexByTag(
    StepperTag.cabins,
    fhBookingSteps,
  );
  const locationStepper = getBookingStepByTag(
    StepperTag.locations,
    fhBookingSteps,
  );

  const yourBookingStepper = getBookingStepByTag(
    StepperTag.yourbooking,
    fhBookingSteps,
  );
  const featuredExtrasStepper = getBookingStepByTag(
    StepperTag.extras,
    fhBookingSteps,
  );
  const isBookingInitiated =
    cabinStepper?.completed || isInterstitialBooking || false; // TODO: this may need revision in case a new booking journey is initiated

  useEffect(() => {
    if (!bookingId) return;
    dispatch(
      getBookingConfirmationAction({
        // to revalidate booking id, if it is invalid it will remove sticky
        bookingId,
        pIIStatusOverride: !!pIIStatusOverride
          ? pIIStatusOverride === "true" || false
          : undefined,
        pIIDepositTypeOverride,
      }),
    );
  }, [bookingId]);

  useEffect(() => {
    const hasLocation = !!location;
    const hasCabin =
      !!(cabin && cabinBedroom) || (!!cabin && isInterstitialBooking);
    const hasSearchDetails = !!guests && !!dateRange;

    const currentStepPage = getBookingStepByUrl(fhBookingSteps);
    const isOnBookingJourney =
      typeof currentStepPage !== "undefined" ||
      interstitialPagePathName === history.asPath;
    const isOnLocationOrCabinPage =
      typeof currentStepPage !== "undefined" && activeBookingStepIndex > 1;

    setIsShowing(
      (hasCabin &&
        hasSearchDetails &&
        !isOnLocationOrCabinPage &&
        reachedBookingSummaryPage) ||
        (hasSearchDetails && !isOnBookingJourney),
    );
  }, [
    location,
    cabin,
    cabinBedroom,
    guests,
    dateRange,
    history,
    activeBookingStepIndex,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(document.location.search);
    const pathname = useNextRouterPathname(history);
    const pIIStatusOverride = queryParams.get("PIIStatusOverride");
    const pIIDepositTypeOverride = queryParams.get("PIIDepositTypeOverride");
    const isOnCabinDetailsPage =
      !!pathname &&
      !!cabinDetailsUrl &&
      pathname ===
        createPathFromUrl(addTrailingSlashToUrl(cabinDetailsUrl) || "");
    const isOnLocationDetailsPage =
      !!pathname &&
      !!locationDetailsUrl &&
      pathname ===
        createPathFromUrl(addTrailingSlashToUrl(locationDetailsUrl) || "");
    if (
      !!pathname &&
      yourBookingStepper?.url ===
        createPathFromUrl(addTrailingSlashToUrl(pathname))
    )
      dispatch(setReachedBookingSummaryPage(true));
    setpIIDepositTypeOverride(pIIDepositTypeOverride);
    setpIIStatusOverride(pIIStatusOverride);
    setIsOnCabinDetailsPage(isOnCabinDetailsPage);
    setIsOnLocationDetailsPage(isOnLocationDetailsPage);
    (!!pathname &&
      cabinStepper?.url ===
        createPathFromUrl(addTrailingSlashToUrl(pathname))) ||
    (!!pathname &&
      locationStepper?.url ===
        createPathFromUrl(addTrailingSlashToUrl(pathname)))
      ? setIsOnLocationsOrCabinsPage(true)
      : setIsOnLocationsOrCabinsPage(false);
  }, [history]);

  const parsedText = (text?: string) => {
    return text ? (
      <DynamicRichContent>
        <div dangerouslySetInnerHTML={makeHtml(text as string)} />
      </DynamicRichContent>
    ) : undefined;
  };

  const makeIcon = (icon?: Maybe<ImageInfo>, size?: string) =>
    icon?.url ? <DynamicFHInlineSvg src={icon.url} size={"24px"} /> : undefined;

  const abandonedBasketProps = {
    closeTitle: isBookingInitiated
      ? bookingJourneyStickySettings?.modalClearBasketTitle || ""
      : bookingJourneyStickySettings?.modalClearSearchTitle || "",
    closeDescription: isBookingInitiated
      ? parsedText(bookingJourneyStickySettings?.modalClearBasketDescription)
      : parsedText(bookingJourneyStickySettings?.modalClearSearchDescription),
    closeConfirmButton: isBookingInitiated
      ? bookingJourneyStickySettings?.modalClearBasketCTALabel
      : bookingJourneyStickySettings?.modalClearSearchCTALabel || "",
    closeCancelButton: bookingJourneyStickySettings?.modalCancelCTALabel || "",
  };

  const makeLocationStickyLabels = (): BookingJourneyStickyLabels => ({
    pet: bookingJourneyStickySettings?.petLabel || "",
    pets: bookingJourneyStickySettings?.petsLabel || "",
    guest: bookingJourneyStickySettings?.guestLabel || "",
    guests: bookingJourneyStickySettings?.guestsLabel || "",
    linkLabel: bookingJourneyStickySettings?.backToSearchCTALabel || "",
    barCTALabel:
      location?.locationName && isOnLocationDetailsPage && !isBookingInitiated
        ? `${bookingJourneyStickySettings?.viewCabinsAtLocationCTALabel} ${location?.locationName}`
        : (isBookingInitiated &&
            bookingJourneyStickySettings?.completeBookingCTALabel) ||
          "",
    locationName: isBookingInitiated ? cabin?.locationName : "",
    cabinTypeName: isBookingInitiated ? cabin?.cabinTypeName : "",

    ...abandonedBasketProps,
  });

  const makeCabinStickyLabels = (cta: string): BookingJourneyStickyLabels => ({
    pet: bookingJourneyStickySettings?.petLabel || "",
    pets: bookingJourneyStickySettings?.petsLabel || "",
    guest: bookingJourneyStickySettings?.guestLabel || "",
    guests: bookingJourneyStickySettings?.guestsLabel || "",
    linkLabel:
      isBookingInitiated && isOnCabinDetailsPage
        ? (!!cta && cta) || ""
        : (isOnCabinDetailsPage &&
            bookingJourneyStickySettings?.backToSearchCTALabel) ||
          "",
    barCTALabel:
      !!cta || isBookingInitiated
        ? (isBookingInitiated &&
            bookingJourneyStickySettings?.completeBookingCTALabel) ||
          cta
        : location?.locationName && !isBookingInitiated && isOnCabinDetailsPage
          ? `${bookingJourneyStickySettings?.bookCabinCTALabel} ${location?.locationName}`
          : bookingJourneyStickySettings?.returnToSearchCTALabel,
    locationName: isBookingInitiated ? cabin?.locationName : "",
    cabinTypeName: isBookingInitiated ? cabin?.cabinTypeName : "",
    ...abandonedBasketProps,
  });

  const makeIcons = (): BookingJourneyStickyIcons => ({
    date: makeIcon(bookingJourneyStickySettings?.dateIcon, "24px") || (
      <CalendarMonthIcon />
    ),
    guest: makeIcon(bookingJourneyStickySettings?.guestsIcon, "24px") || (
      <PersonIcon />
    ),
    cabin: makeIcon(bookingJourneyStickySettings?.cabinTypeIcon, "24px") || (
      <HomeIcon />
    ),
    location: makeIcon(bookingJourneyStickySettings?.locationIcon, "24px") || (
      <LocationOnIcon />
    ),
  });

  const makeRedirectUrl = (url: string, overrideLocations?: string[]) => {
    const locationIds: string[] =
      overrideLocations || selectedLocationIds || [];

    if (!!locationIds.length) {
      return filterXSS(
        url +
          generateBookingQueryString({
            startDate: dateRange?.startDateISO || "",
            endDate: dateRange?.endDateISO || "",
            locationIds,
            guests: guests,
            dda: dda,
          }),
      );
    }
    return url;
  };

  const changeCabin = () => {
    dispatch(
      setBookingBedroom({
        cabin,
        cabinBedroom,
      }),
    );
    dispatch(
      setActiveStepperSetting({
        activeJourneyStep: activeBookingStepIndex + 1,
        activeJourney: JourneyTypes.bookingJourney,
      }),
    );
  };

  let stickyProps: JourneyStickyProps | undefined = undefined;

  const stickyDefaultProps = {
    show: isShowing,
    isoDateFrom: dateRange?.startDateISO || undefined,
    isoDateTo: dateRange?.endDateISO || undefined,
    noOfGuests: !!guests
      ? [guests.adults, guests.children].reduce((a, b) => a + b)
      : 0,
    noOfPets: guests?.pets || undefined,
    ctaButtonDisabled: false,
    title: isBookingInitiated
      ? bookingJourneyStickySettings?.basketTitle || ""
      : bookingJourneyStickySettings?.searchTitle || "",
    showLabel: isBookingInitiated
      ? bookingJourneyStickySettings?.viewBasketCTALabel
      : bookingJourneyStickySettings?.viewSearchCTALabel,
    hideLabel: isBookingInitiated
      ? bookingJourneyStickySettings?.hideBasketCTALabel
      : bookingJourneyStickySettings?.hideSearchCTALabel,
    onCloseConfirm: () => {
      if (bookingId) {
        dispatch(removeUnconfirmedBooking({ bookingId }));
        if (vouchers?.length > 0)
          removeAllGiftVouchersFromCurrentBooking(dispatch, vouchers);
      }
      dispatch(setReachedBookingSummaryPage(false));
      const url = `${process.env.NEXT_PUBLIC_BASE_URL}${history.asPath.replace(
        "/",
        "",
      )}`;
      const referrer = isBrowser() ? document.referrer : "";
      resetBookingFunnel({ url, referrer });
      dispatch(
        resetActiveBasketAndBooking({
          isExpOn: true,
          isOnCabinOrLocationsResultsPage: isOnLocationsOrCabinsPage,
        }),
      );
      dispatch(resetReservationFromReferral());
      dispatch(resetCabinIfo());
      dispatch(resetMandatoryExtraInteractions());
    },
    experiments: experiments,
  };

  const getLocationSticky = () => {
    bookingId
      ? false
      : location?.locationId?.length
        ? dispatch(setSelectedLocationIds([location?.locationId]))
        : dispatch(setSelectedLocationIds([cabin?.locationId]));
    dispatch(
      setActiveStepperSetting({
        activeJourneyStep: cabinStepperIndex,
        activeJourney: JourneyTypes.bookingJourney,
      }),
    );
    history.push({
      pathname: cabinStepper?.url || "",
      search: generateBookingQueryString({
        startDate: dateRange?.startDateISO || "",
        endDate: dateRange?.endDateISO || "",
        locationIds: [location?.locationId],
        guests: guests,
        dda: dda,
      }),
    });
  };

  switch (activeStep && activeStep.tag) {
    case StepperTag.locations:
      stickyProps = {
        ...stickyDefaultProps,
        linkOnClick: () => {
          if (isBookingInitiated) {
            history.push(makeRedirectUrl(yourBookingStepper?.url || ""));
          } else {
            dispatch(
              setActiveStepperSetting({
                activeJourneyStep: cabinStepperIndex,
                activeJourney: JourneyTypes.bookingJourney,
              }),
            );
            history.push(
              makeRedirectUrl(
                locationStepper?.url || "",
                tempSelectedLocationIds,
              ),
            );
          }
        },
        barCTAOnClick: () => {
          bookingId
            ? history.push(yourBookingStepper?.url || "")
            : getLocationSticky();
        },
        labels: makeLocationStickyLabels(),
        icons: makeIcons(),
      };
      break;
    case StepperTag.cabins:
      const isActiveBasket = reservationStateCabinId && isOnCabinDetailsPage;
      const cabinCta = isOnCabinDetailsPage
        ? `${
            isActiveBasket
              ? bookingJourneyStickySettings?.changeCabinCTALabel
              : bookingJourneyStickySettings?.bookCabinCTALabel
          } ${
            isActiveBasket
              ? `(${formatPrice(cabinBedroom?.postDiscountPrice)})`
              : formatPrice(cabinBedroom?.postDiscountPrice)
          }`
        : "";
      stickyProps = {
        ...stickyDefaultProps,
        linkOnClick: () => {
          if (
            isBookingInitiated &&
            isOnCabinDetailsPage &&
            yourBookingStepper?.url &&
            cabin &&
            cabinBedroom
          ) {
            changeCabin();
            history.push(yourBookingStepper.url);
          } else {
            history.push(makeRedirectUrl(locationStepper?.url || ""));
          }
        },
        barCTAOnClick: () => {
          if (
            (isBookingInitiated || isOnCabinDetailsPage) &&
            yourBookingStepper?.url &&
            cabin &&
            cabinBedroom
          ) {
            if (!(isBookingInitiated && isOnCabinDetailsPage))
              bookingId ? false : changeCabin();

            history.push(yourBookingStepper.url);
          } else if (!isOnCabinDetailsPage) {
            history.push(makeRedirectUrl(cabinStepper?.url || ""));
          } else {
            // return to search with selected location from location details
            dispatch(setSelectedLocationIds([location.locationId]));
            history.push(
              makeRedirectUrl(cabinStepper?.url || "", [location.locationId]),
            );
          }
        },
        labels: makeCabinStickyLabels(cabinCta),
        icons: makeIcons(),
        ctaButtonDisabled: !!cabinCta
          ? !(
              cabinBedroom?.isAvailable &&
              (cabinBedroom?.numberOfCabins || 0) > 0
            )
          : false,
      };
      break;
    case StepperTag.yourbooking:
      stickyProps = {
        ...stickyDefaultProps,
        barCTAOnClick: () => {
          history.push(yourBookingStepper?.url || "");
        },
        labels: makeLocationStickyLabels(),
        icons: makeIcons(),
      };
      break;
    case StepperTag.extras:
    case StepperTag.login:
    case StepperTag.payment:
      stickyProps = {
        ...stickyDefaultProps,
        barCTAOnClick: () => {
          history.push(featuredExtrasStepper?.url || "");
        },
        labels: makeLocationStickyLabels(),
        icons: makeIcons(),
      };
      break;
  }

  if (!stickyProps) return null;
  return <MemoizedSticky {...stickyProps} />;
};

const mapStateToProps = (state: RootState) =>
  ({
    fhBookingSteps: fhBookingStepsSelector(state),
    activeStep: activeStepSelector(state),
    dateRange: stickyDateRangeSelector(state),
    guests: stickyGuestsSelector(state),
    dda: stickyDdaSelector(state),
    cabin: stickyCabinSelector(state),
    cabinBedroom: stickyCabinBedroomSelector(state),
    location: stickyLocationSelector(state),
    cabinDetailsUrl: stickyCabinDetailsUrlSelector(state),
    locationDetailsUrl: stickyLocationDetailsUrlSelector(state),
    bookingJourneyStickySettings: bookingJourneyStickySettingsSelector(state),
    reservationStateCabinId: reservationStateCabinIdSelector(state),
    expiry: reservationExpirySelector(state) || stickyExpirySelector(state),
    isInterstitialBooking: isInterstitialBookingSelector(state),
    interstitialPagePathName: getInterstitialPagePathSelector(state),
    bookingId: bookingPersistedStateSelector(state).bookingId,
  }) as BookingJourneyStickySelectorProps;

export const StickyBookingWidget = connect(mapStateToProps)(
  BookingJourneyStickyComponent,
);
